/**
 * Hover help. Any element with data-help="key" shows the matching entry from
 * the help registry after a short delay (also on keyboard focus).
 */
import { signal } from '@preact/signals';
import { useEffect, useRef, useState } from 'preact/hooks';
import { HELP } from './help';

export const tooltipsEnabled = signal(true);
const DELAY = 450;

interface Tip {
  key: string;
  rect: DOMRect;
}

export function Tooltip() {
  const [tip, setTip] = useState<Tip | null>(null);
  const timer = useRef(0);
  const current = useRef<Element | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: 0, top: 0 });

  useEffect(() => {
    const show = (el: Element) => {
      const key = el.getAttribute('data-help');
      if (!key || !HELP[key] || !tooltipsEnabled.value) return;
      clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        if (current.current === el && el.isConnected) setTip({ key, rect: el.getBoundingClientRect() });
      }, DELAY);
    };
    const hide = () => {
      clearTimeout(timer.current);
      current.current = null;
      setTip(null);
    };
    const over = (e: Event) => {
      const el = (e.target as Element | null)?.closest?.('[data-help]') ?? null;
      if (el === current.current) return;
      current.current = el;
      setTip(null);
      clearTimeout(timer.current);
      if (el) show(el);
    };
    const focus = (e: FocusEvent) => {
      const el = (e.target as Element | null)?.closest?.('[data-help]') ?? null;
      if (el && (e.target as Element).matches(':focus-visible')) {
        current.current = el;
        show(el);
      }
    };
    document.addEventListener('pointerover', over);
    document.addEventListener('pointerdown', hide, true);
    document.addEventListener('wheel', hide, { passive: true, capture: true });
    document.addEventListener('focusin', focus);
    document.addEventListener('focusout', hide);
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && hide();
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerover', over);
      document.removeEventListener('pointerdown', hide, true);
      document.removeEventListener('wheel', hide, true);
      document.removeEventListener('focusin', focus);
      document.removeEventListener('focusout', hide);
      document.removeEventListener('keydown', esc);
    };
  }, []);

  useEffect(() => {
    if (!tip || !box.current) return;
    const b = box.current.getBoundingClientRect();
    const r = tip.rect;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = r.bottom + 8;
    if (top + b.height > vh - 8) top = Math.max(8, r.top - b.height - 8);
    let left = r.left + Math.min(r.width / 2, 40) - 12;
    left = Math.max(8, Math.min(left, vw - b.width - 8));
    setPos({ left, top });
  }, [tip]);

  if (!tip) return null;
  const h = HELP[tip.key];
  return (
    <div class="tooltip" role="tooltip" ref={box} style={{ left: `${pos.left}px`, top: `${pos.top}px` }} data-testid="tooltip">
      <div class="tooltip-title">
        {h.title}
        {h.keys && <kbd>{h.keys}</kbd>}
      </div>
      <div class="tooltip-body">{h.text}</div>
      {h.how && <div class="tooltip-how">{h.how}</div>}
    </div>
  );
}
